/**
 * LauncherDrawer — Gaveta Lateral Retrátil para disparo de novas solicitações.
 *
 * Substitui a tela cheia /entradas (Prompt 31).
 *
 * Fases internas:
 *  1) catalog  → busca + lista de processos por categoria
 *  2) form     → título e prioridade; ao criar, abre a primeira etapa
 *
 * Slide direita → esquerda. fixed inset-y-0 right-0 w-[450px].
 * Estado controlado por useUIStore.isLauncherDrawerOpen.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  Briefcase,
  Check,
  ChevronRight,
  ClipboardList,
  FileCheck,
  FileText,
  GitBranch,
  Loader2,
  Play,
  Receipt,
  Search,
  Settings,
  Star,
  ShoppingCart,
  Users,
  X,
  Zap,
} from 'lucide-react';
import { useUIStore } from '../../store/useUIStore';
import { useOrganizationId } from '../../contexts/WorkspaceContext';
import { useAuth } from '../../context/AuthContext';
import { useAppToast } from '../AppToast';
import { EntryFlowService } from '../../services/entryFlowService';
import type { EntryFlow } from '../../services/entryFlowService';
import { formatLauncherLastExecution } from '../../modules/minha-mesa/utils/taskDisplayUtils';
import { createExecutionInstance } from '../../services/executionService';
import { useExecutionSelection } from '../../contexts/ExecutionSelectionContext';

// ─── Icon resolution (entry_icon key → JSX) ───────────────────
const ICON_MAP: Record<string, React.ReactNode> = {
  receipt: <Receipt size={12} />,
  clipboard: <ClipboardList size={12} />,
  process: <GitBranch size={12} />,
  settings: <Settings size={12} />,
  users: <Users size={12} />,
  cart: <ShoppingCart size={12} />,
  briefcase: <Briefcase size={12} />,
  document: <FileText size={12} />,
  check: <FileCheck size={12} />,
  zap: <Zap size={12} />,
};

function resolveIcon(iconKey: string | null, category: string | null): React.ReactNode {
  if (iconKey && ICON_MAP[iconKey]) return ICON_MAP[iconKey];
  if (category && ICON_MAP[category]) return ICON_MAP[category];
  return <Play size={12} />;
}

// ─── Category color tokens ────────────────────────────────────
const CATEGORY_COLORS: Record<string, { bg: string; text: string; border: string; label: string }> = {
  financeiro: { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200', label: 'Financeiro' },
  vendas: { bg: 'bg-indigo-50', text: 'text-indigo-700', border: 'border-indigo-200', label: 'Vendas' },
  geral: { bg: 'bg-slate-100', text: 'text-slate-700', border: 'border-slate-200', label: 'Geral' },
  operacoes: { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200', label: 'Operações' },
  operacional: { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200', label: 'Operacional' },
  rh: { bg: 'bg-cyan-50', text: 'text-cyan-700', border: 'border-cyan-200', label: 'RH' },
  ti: { bg: 'bg-violet-50', text: 'text-violet-700', border: 'border-violet-200', label: 'TI' },
  logistica: { bg: 'bg-orange-50', text: 'text-orange-700', border: 'border-orange-200', label: 'Logística' },
};

export function categoryToken(category: string | null) {
  const key = (category || 'geral').toLowerCase();
  return CATEGORY_COLORS[key] ?? CATEGORY_COLORS.geral;
}

// ─── Priority horizontal selector ─────────────────────────────
type Priority = 'baixa' | 'media' | 'alta' | 'critica';

const PRIORITY_OPTIONS: { value: Priority; label: string; tone: string }[] = [
  { value: 'baixa', label: 'Baixa', tone: 'border-slate-200 text-slate-700 hover:bg-slate-50' },
  { value: 'media', label: 'Média', tone: 'border-blue-200 text-blue-700 hover:bg-blue-50' },
  { value: 'alta', label: 'Alta', tone: 'border-amber-300 text-amber-700 hover:bg-amber-50' },
  { value: 'critica', label: 'Crítica', tone: 'border-rose-300 text-rose-700 hover:bg-rose-50' },
];

const PRIORITY_ACTIVE: Record<Priority, string> = {
  baixa: 'bg-slate-700 text-white border-slate-700',
  media: 'bg-blue-600 text-white border-blue-600',
  alta: 'bg-amber-600 text-white border-amber-600',
  critica: 'bg-rose-600 text-white border-rose-600',
};

// ─────────────────────────────────────────────────────────────
type DrawerPhase = 'catalog' | 'form';

export const LauncherDrawer: React.FC = () => {
  const isOpen = useUIStore((s) => s.isLauncherDrawerOpen);
  const closeDrawer = useUIStore((s) => s.closeLauncherDrawer);
  const launcherSearchQuery = useUIStore((s) => s.launcherSearchQuery);
  const setLauncherSearchQuery = useUIStore((s) => s.setLauncherSearchQuery);

  const orgId = useOrganizationId();
  const { user } = useAuth();
  const { showToast, ToastContainer } = useAppToast();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { selectTask, selectInstance } = useExecutionSelection();

  const [phase, setPhase] = useState<DrawerPhase>('catalog');
  const [selectedProcessForLaunch, setSelectedProcessForLaunch] = useState<EntryFlow | null>(null);
  const [title, setTitle] = useState('');
  const [priority, setPriority] = useState<Priority>('media');
  const [creating, setCreating] = useState(false);

  const searchRef = useRef<HTMLInputElement>(null);
  const titleRef = useRef<HTMLInputElement>(null);

  // Reset state on close
  useEffect(() => {
    if (!isOpen) {
      setPhase('catalog');
      setSelectedProcessForLaunch(null);
      setTitle('');
      setPriority('media');
    }
  }, [isOpen]);

  // Focus on phase transition
  useEffect(() => {
    if (!isOpen) return;
    if (phase === 'catalog') {
      requestAnimationFrame(() => searchRef.current?.focus());
    } else {
      requestAnimationFrame(() => titleRef.current?.focus());
    }
  }, [isOpen, phase]);

  // ESC closes drawer / Esc on form returns to catalog
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (phase === 'form') {
        setPhase('catalog');
      } else {
        closeDrawer();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isOpen, phase, closeDrawer]);

  // ─── Fetch entry flows ───────────────────────────────────
  const { data: flows = [], isLoading } = useQuery({
    queryKey: ['entry-flows', orgId, user?.role],
    queryFn: () => EntryFlowService.getEntryFlows(orgId, user?.role),
    enabled: isOpen && !!orgId,
    staleTime: 60_000,
  });

  // ─── Filter + group ──────────────────────────────────────
  const [favoriteIds, setFavoriteIds] = useState<string[]>(() => {
    try {
      const raw = localStorage.getItem('dataflow-launcher-favorites');
      return raw ? (JSON.parse(raw) as string[]) : [];
    } catch {
      return [];
    }
  });

  const toggleFavorite = useCallback((flowId: string) => {
    setFavoriteIds((prev) => {
      const next = prev.includes(flowId) ? prev.filter((id) => id !== flowId) : [...prev, flowId];
      localStorage.setItem('dataflow-launcher-favorites', JSON.stringify(next));
      return next;
    });
  }, []);

  const filteredFlows = useMemo(() => {
    const q = launcherSearchQuery.trim().toLowerCase();
    if (!q) return flows;
    return flows.filter(
      (f) =>
        f.entryLabel.toLowerCase().includes(q) ||
        f.name.toLowerCase().includes(q) ||
        (f.entryDescription?.toLowerCase().includes(q) ?? false) ||
        (f.category?.toLowerCase().includes(q) ?? false) ||
        f.entryRoles.some((role) => role.toLowerCase().includes(q)),
    );
  }, [flows, launcherSearchQuery]);

  const favoriteFlows = useMemo(
    () => filteredFlows.filter((f) => favoriteIds.includes(f.id)),
    [filteredFlows, favoriteIds],
  );

  const grouped = useMemo(() => {
    const map = new Map<string, EntryFlow[]>();
    for (const f of filteredFlows) {
      const key = (f.category || 'geral').toLowerCase();
      const arr = map.get(key) ?? [];
      arr.push(f);
      map.set(key, arr);
    }
    return Array.from(map.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, [filteredFlows]);

  const categoryCounts = useMemo(() => {
    const map = new Map<string, number>();
    for (const f of flows) {
      const key = (f.category || 'geral').toLowerCase();
      map.set(key, (map.get(key) ?? 0) + 1);
    }
    return map;
  }, [flows]);

  // ─── Handlers ────────────────────────────────────────────
  const handleSelectFlow = useCallback((flow: EntryFlow) => {
    setSelectedProcessForLaunch(flow);
    // Pre-fill title with flow label + date
    const dd = String(new Date().getDate()).padStart(2, '0');
    const mm = String(new Date().getMonth() + 1).padStart(2, '0');
    setTitle(`${flow.entryLabel} — ${dd}/${mm}`);
    setPriority('media');
    setPhase('form');
  }, []);

  const handleBackToCatalog = useCallback(() => {
    setPhase('catalog');
  }, []);

  const handleCreate = useCallback(async () => {
    if (!selectedProcessForLaunch || !title.trim() || creating) return;
    setCreating(true);
    try {
      const created = await createExecutionInstance({
        organizationId: orgId,
        blueprintId: selectedProcessForLaunch.id,
        versionId: selectedProcessForLaunch.latestVersionId ?? undefined,
        title: title.trim(),
        priority,
        initiatedBy: user?.id,
        contextData: {},
      });

      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['tasks'] }),
        queryClient.invalidateQueries({ queryKey: ['minha-mesa'] }),
        queryClient.invalidateQueries({ queryKey: ['execution'] }),
        queryClient.invalidateQueries({ queryKey: ['entry-flows'] }),
      ]);

      closeDrawer();

      if (created.firstTaskId) {
        selectInstance(created.id);
        selectTask(created.firstTaskId);
        try { localStorage.setItem('workflow.minha-mesa.viewMode', 'list'); } catch { /* noop */ }
        navigate('/minha-mesa');
        showToast('success', `Solicitação "${title.trim()}" aberta na primeira etapa`);
      } else {
        showToast('success', `Solicitação "${title.trim()}" criada com sucesso`);
      }
    } catch (e: unknown) {
      showToast('error', e instanceof Error ? e.message : 'Erro ao criar solicitação');
    } finally {
      setCreating(false);
    }
  }, [selectedProcessForLaunch, title, priority, orgId, user?.id, creating, showToast, queryClient, closeDrawer, selectInstance, selectTask, navigate]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && (e.ctrlKey || e.metaKey) && phase === 'form' && title.trim()) {
        e.preventDefault();
        handleCreate();
      }
    },
    [phase, title, handleCreate],
  );

  if (!isOpen) return null;

  const flowCategoryToken = selectedProcessForLaunch ? categoryToken(selectedProcessForLaunch.category) : null;

  return createPortal(
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-40 bg-slate-900/30 backdrop-blur-[2px] transition-opacity"
        onClick={closeDrawer}
        aria-hidden="true"
      />

      {/* Drawer */}
      <aside
        role="dialog"
        aria-modal="true"
        aria-label="Nova Solicitação"
        onKeyDown={handleKeyDown}
        className="fixed inset-y-0 right-0 z-50 flex w-[450px] max-w-[100vw] flex-col border-l border-slate-200 bg-white shadow-2xl transform transition-transform duration-200 ease-out"
      >
        {/* ═════ Header ═════ */}
        <header className="flex shrink-0 items-center justify-between border-b border-slate-200 px-5 py-3">
          <div className="flex items-center gap-2">
            {phase === 'form' && (
              <button
                type="button"
                onClick={handleBackToCatalog}
                className="flex h-7 w-7 items-center justify-center rounded-sm text-slate-500 hover:bg-slate-100 hover:text-slate-700 transition-colors"
                title="Voltar para o catálogo"
                aria-label="Voltar para o catálogo"
              >
                <ArrowLeft size={14} />
              </button>
            )}
            <div>
              <h2 className="text-[13px] font-bold uppercase tracking-[0.08em] text-slate-800">
                {phase === 'catalog' ? 'Nova Solicitação' : 'Abrir Processo'}
              </h2>
              <p className="text-[10px] font-medium uppercase tracking-[0.12em] text-slate-400">
                {phase === 'catalog' ? 'Selecione o processo' : selectedProcessForLaunch?.entryLabel ?? ''}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={closeDrawer}
            className="flex h-7 w-7 items-center justify-center rounded-sm text-slate-500 hover:bg-slate-100 hover:text-slate-700 transition-colors"
            title="Fechar (Esc)"
            aria-label="Fechar"
          >
            <X size={14} />
          </button>
        </header>

        {/* ═════ Body ═════ */}
        {phase === 'catalog' ? (
          <CatalogPhase
            searchRef={searchRef}
            searchValue={launcherSearchQuery}
            onSearchChange={setLauncherSearchQuery}
            grouped={grouped}
            favoriteFlows={favoriteFlows}
            favoriteIds={favoriteIds}
            onToggleFavorite={toggleFavorite}
            categoryCounts={categoryCounts}
            isLoading={isLoading}
            onSelectFlow={handleSelectFlow}
          />
        ) : (
          <FormPhase
            titleRef={titleRef}
            flow={selectedProcessForLaunch!}
            categoryToken={flowCategoryToken!}
            title={title}
            onTitleChange={setTitle}
            priority={priority}
            onPriorityChange={setPriority}
            creating={creating}
            onSubmit={handleCreate}
            onCancel={handleBackToCatalog}
          />
        )}
      </aside>

      <ToastContainer />
    </>,
    document.body,
  );
};

// ─────────────────────────────────────────────────────────────
// ─── Phase 1: Catalog ────────────────────────────────────────
// ─────────────────────────────────────────────────────────────
interface CatalogPhaseProps {
  searchRef: React.RefObject<HTMLInputElement>;
  searchValue: string;
  onSearchChange: (v: string) => void;
  grouped: [string, EntryFlow[]][];
  favoriteFlows: EntryFlow[];
  favoriteIds: string[];
  onToggleFavorite: (flowId: string) => void;
  categoryCounts: Map<string, number>;
  isLoading: boolean;
  onSelectFlow: (flow: EntryFlow) => void;
}

export const CatalogPhase: React.FC<CatalogPhaseProps> = ({
  searchRef,
  searchValue,
  onSearchChange,
  grouped,
  favoriteFlows,
  favoriteIds,
  onToggleFavorite,
  categoryCounts,
  isLoading,
  onSelectFlow,
}) => {
  return (
    <>
      {/* Search */}
      <div className="shrink-0 border-b border-slate-100 px-5 py-3">
        <div className="relative">
          <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            ref={searchRef}
            type="text"
            value={searchValue}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder="Nome, categoria, tags ou descrição..."
            className="h-8 w-full rounded-sm border border-slate-200 bg-white pl-7 pr-3 text-[12px] text-slate-800 placeholder:text-slate-400 focus:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-100"
          />
        </div>
      </div>

      {/* Catalog list */}
      <div className="min-h-0 flex-1 overflow-y-auto px-5 py-3">
        {isLoading ? (
          <div className="flex h-32 items-center justify-center">
            <Loader2 size={18} className="animate-spin text-slate-400" />
          </div>
        ) : grouped.length === 0 ? (
          <div className="flex h-40 flex-col items-center justify-center gap-2 px-6 text-center">
            <Search size={20} className="text-slate-300" />
            <p className="text-[12px] font-semibold text-slate-600">Nenhum processo disponível</p>
            <p className="text-[11px] leading-relaxed text-slate-500">
              {searchValue.trim()
                ? 'Nenhum resultado para a busca. Tente outros termos.'
                : 'Publique um Blueprint no Atelier de Processos ou marque-o como ponto de entrada para iniciá-lo aqui.'}
            </p>
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            {favoriteFlows.length > 0 && (
              <section>
                <h3 className="mb-1.5 text-[10px] font-bold uppercase tracking-[0.12em] text-amber-600">
                  Favoritos ({favoriteFlows.length})
                </h3>
                <ul className="flex flex-col gap-1">
                  {favoriteFlows.map((flow) => (
                    <FlowCatalogRow
                      key={`fav-${flow.id}`}
                      flow={flow}
                      isFavorite
                      onToggleFavorite={onToggleFavorite}
                      onSelectFlow={onSelectFlow}
                    />
                  ))}
                </ul>
              </section>
            )}
            {grouped.map(([categoryKey, flowsInCat]) => {
              const tok = categoryToken(categoryKey);
              const totalInCategory = categoryCounts.get(categoryKey) ?? flowsInCat.length;
              return (
                <section key={categoryKey}>
                  <h3 className="mb-1.5 text-[10px] font-bold uppercase tracking-[0.12em] text-slate-400">
                    {tok.label} ({totalInCategory})
                  </h3>
                  <ul className="flex flex-col gap-1">
                    {flowsInCat.map((flow) => (
                      <FlowCatalogRow
                        key={flow.id}
                        flow={flow}
                        isFavorite={favoriteIds.includes(flow.id)}
                        onToggleFavorite={onToggleFavorite}
                        onSelectFlow={onSelectFlow}
                      />
                    ))}
                  </ul>
                </section>
              );
            })}
          </div>
        )}
      </div>
    </>
  );
};

function FlowCatalogRow({
  flow,
  isFavorite,
  onToggleFavorite,
  onSelectFlow,
}: {
  flow: EntryFlow;
  isFavorite: boolean;
  onToggleFavorite: (id: string) => void;
  onSelectFlow: (flow: EntryFlow) => void;
}) {
  const tok = categoryToken(flow.category);
  const sentence = flow.entryDescription?.trim() || '';
  const lastLabel = flow.lastExecutedAt ? formatLauncherLastExecution(flow.lastExecutedAt) : '';
  const showLast = Boolean(lastLabel) && lastLabel !== '—' && lastLabel !== 'Nunca';
  return (
    <li>
      <div className="group flex w-full items-center gap-1 rounded-md px-1 py-1 transition-colors hover:bg-slate-50">
        <button
          type="button"
          onClick={() => onSelectFlow(flow)}
          className="flex min-w-0 flex-1 items-center gap-2.5 py-1.5 pl-1 text-left"
        >
          <span
            className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-sm border ${tok.bg} ${tok.text} ${tok.border}`}
          >
            {resolveIcon(flow.entryIcon, flow.category)}
          </span>
          <span className="min-w-0 flex-1">
            <span className="block truncate text-[13px] font-medium text-slate-800">
              {flow.entryLabel}
            </span>
            {sentence && (
              <span className="mt-0.5 block truncate text-[11px] text-slate-500">{sentence}</span>
            )}
            {showLast && (
              <span className="mt-0.5 block truncate text-[10px] text-slate-400">
                Última {lastLabel.toLowerCase()}
              </span>
            )}
          </span>
          <ChevronRight size={14} className="shrink-0 text-slate-300 group-hover:text-slate-500" />
        </button>
        <button
          type="button"
          onClick={() => onToggleFavorite(flow.id)}
          className={`shrink-0 rounded p-1.5 transition ${
            isFavorite
              ? 'text-amber-500'
              : 'text-slate-300 opacity-0 group-hover:opacity-100 hover:text-amber-400'
          }`}
          title={isFavorite ? 'Remover dos favoritos' : 'Adicionar aos favoritos'}
          aria-label={isFavorite ? 'Remover dos favoritos' : 'Adicionar aos favoritos'}
        >
          <Star size={12} fill={isFavorite ? 'currentColor' : 'none'} />
        </button>
      </div>
    </li>
  );
}

// ─────────────────────────────────────────────────────────────
// ─── Phase 2: Form ───────────────────────────────────────────
// ─────────────────────────────────────────────────────────────
interface FormPhaseProps {
  titleRef: React.RefObject<HTMLInputElement>;
  flow: EntryFlow;
  categoryToken: { bg: string; text: string; border: string; label: string };
  title: string;
  onTitleChange: (v: string) => void;
  priority: Priority;
  onPriorityChange: (p: Priority) => void;
  creating: boolean;
  onSubmit: () => void;
  onCancel: () => void;
  variant?: 'drawer' | 'sheet';
}

export const FormPhase: React.FC<FormPhaseProps> = ({
  titleRef,
  flow,
  categoryToken: tok,
  title,
  onTitleChange,
  priority,
  onPriorityChange,
  creating,
  onSubmit,
  onCancel,
  variant = 'drawer',
}) => {
  const canSubmit = title.trim().length > 0 && !creating;
  const sheet = variant === 'sheet';

  return (
    <>
      {/* Body */}
      <div className={sheet ? 'px-5 py-4' : 'min-h-0 flex-1 overflow-y-auto px-5 py-4'}>
        {/* Process header */}
        <div className="mb-4 flex items-start gap-3">
          <span
            className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-sm border ${tok.bg} ${tok.text} ${tok.border}`}
          >
            {resolveIcon(flow.entryIcon, flow.category)}
          </span>
          <div className="min-w-0 flex-1">
            <h3 className="text-[14px] font-bold text-slate-800">{flow.entryLabel}</h3>
            {flow.entryDescription && (
              <p className="mt-0.5 text-[11px] text-slate-500">{flow.entryDescription}</p>
            )}
            {/* Metadata badges */}
            <div className="mt-2 flex flex-wrap gap-1">
              <span className={`inline-flex items-center gap-1 rounded-sm border px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide ${tok.bg} ${tok.text} ${tok.border}`}>
                {tok.label}
              </span>
              {flow.versionSemver && (
                <span className="inline-flex items-center gap-1 rounded-sm border border-slate-200 bg-slate-50 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-slate-600">
                  v{flow.versionSemver}
                </span>
              )}
              {flow.usageCount > 0 && (
                <span className="inline-flex items-center gap-1 rounded-sm border border-slate-200 bg-slate-50 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-slate-600">
                  {flow.usageCount} execuções
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Título */}
        <div className="mb-4">
          <label className="mb-1 block text-[10px] font-bold uppercase tracking-[0.12em] text-slate-500">
            Título da Solicitação
          </label>
          <input
            ref={titleRef}
            type="text"
            value={title}
            onChange={(e) => onTitleChange(e.target.value)}
            placeholder="Descreva resumidamente a solicitação"
            className="h-9 w-full rounded-sm border border-slate-200 bg-white px-2.5 text-[12px] text-slate-800 placeholder:text-slate-400 focus:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-100"
          />
        </div>

        {/* Prioridade horizontal */}
        <div className="mb-4">
          <label className="mb-1 block text-[10px] font-bold uppercase tracking-[0.12em] text-slate-500">
            Prioridade
          </label>
          <div className="grid grid-cols-4 gap-1.5">
            {PRIORITY_OPTIONS.map((opt) => {
              const active = priority === opt.value;
              return (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => onPriorityChange(opt.value)}
                  className={`h-8 rounded-sm border text-[11px] font-semibold transition-colors ${
                    active ? PRIORITY_ACTIVE[opt.value] : `bg-white ${opt.tone}`
                  }`}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
        </div>

        {!sheet && (
          <button
            type="button"
            onClick={onCancel}
            className="inline-flex items-center gap-1 text-[11px] text-slate-500 hover:text-slate-700 transition-colors"
          >
            <ArrowLeft size={11} /> Voltar para o Catálogo
          </button>
        )}
      </div>

      {sheet ? (
        <div className="flex items-center justify-between gap-3 border-t border-slate-100 px-5 py-3 dark:border-[var(--border-default)]">
          <button
            type="button"
            onClick={onCancel}
            className="inline-flex items-center gap-1 text-[11px] text-slate-500 transition-colors hover:text-slate-700"
          >
            <ArrowLeft size={11} /> Voltar para o Catálogo
          </button>
          <div className="flex items-center gap-3">
            <span className="text-[10px] text-slate-400">Ctrl+Enter · Esc para voltar</span>
            <button
              type="button"
              onClick={onSubmit}
              disabled={!canSubmit}
              className="inline-flex h-9 items-center justify-center gap-2 rounded-sm bg-emerald-600 px-4 text-[12px] font-bold text-white transition-colors hover:bg-emerald-700 disabled:cursor-not-allowed disabled:bg-slate-300"
            >
              {creating ? (
                <>
                  <Loader2 size={14} className="animate-spin" /> Criando...
                </>
              ) : (
                <>
                  <Check size={14} /> Criar e Iniciar
                </>
              )}
            </button>
          </div>
        </div>
      ) : (
      <footer className="shrink-0 border-t border-slate-200 bg-slate-50 px-5 py-3">
        <button
          type="button"
          onClick={onSubmit}
          disabled={!canSubmit}
          className="flex h-10 w-full items-center justify-center gap-2 rounded-sm bg-emerald-600 text-[13px] font-bold text-white transition-colors hover:bg-emerald-700 disabled:cursor-not-allowed disabled:bg-slate-300"
        >
          {creating ? (
            <>
              <Loader2 size={14} className="animate-spin" /> Criando...
            </>
          ) : (
            <>
              <Check size={14} /> Criar e Iniciar
            </>
          )}
        </button>
        <p className="mt-1.5 text-center text-[10px] text-slate-400">
          Ctrl+Enter para confirmar · Esc para voltar
        </p>
      </footer>
      )}
    </>
  );
};
